/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { getGrabXValue } from "./getter"
import { TFacets, GrabXDataTypes } from "./types"

/**
 * Custom hook to fetch GrabX feature flags using React Query
 *
 * @template T - The type of the feature flag value
 * @param {string} variableName - The GrabX variable name
 * @param {GrabXDataTypes} dataType - The data type (Boolean, String, Number, Json)
 * @param {T} defaultValue - Default value if flag is not found
 * @param {object} [facets] - Optional facets (e.g., merchant, country)
 * @param {boolean} [enabled] - Whether the query should run (default: true)
 * @param {object} [queryOptions] - Optional React Query options to override defaults
 * @returns {object} React Query result with data, isLoading, error, etc.
 *
 * @example
 * // Boolean flag with merchant facet
 * const { data: isEnabled, isLoading } = useGrabXFeatureFlag<boolean>(
 *   "enable_new_feature",
 *   GrabXDataTypes.Boolean,
 *   false,
 *   { [FacetTypes.Merchant]: merchantID }
 * )
 *
 */
export const useGrabXFeatureFlag = <T = any>(
  variableName: string,
  dataType: GrabXDataTypes,
  defaultValue: T,
  facets?: TFacets,
  enabled?: boolean,
  queryOptions?: Omit<
    UseQueryOptions<T, Error, T>,
    "queryKey" | "queryFn" | "enabled"
  >,
) => {
  return useQuery<T, Error>({
    queryKey: ["GRABX_FEATURE_FLAG", variableName, facets],
    queryFn: async () => {
      const value = await getGrabXValue<T>(
        variableName,
        dataType,
        defaultValue,
        facets,
      )

      return value
    },
    enabled,
    staleTime: Infinity, // Never mark as stale
    cacheTime: Infinity, // Kkeep in cache for entire session
    ...queryOptions,
  })
}
